import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import inboundApi from '../../config/inboundApi';

const STATUS_OPTIONS = ['new', 'open', 'pending', 'solved', 'closed'];
const PRIORITY_OPTIONS = ['low', 'normal', 'high', 'urgent'];

export default function TicketPropertiesSidebar({ ticket, staffList, currentUserEmail, onUpdate }) {
  const [tagInput, setTagInput] = useState('');

  const updateField = async (endpoint, body) => {
    try {
      const res = await inboundApi.put(`/tickets/${ticket.id}/${endpoint}`, body);
      onUpdate(res.data);
    } catch (err) {
      console.error(`Failed to update ${endpoint}:`, err);
    }
  };

  const handleAssigneeChange = (email) => {
    const staff = staffList.find((s) => s.email === email);
    updateField('assignee', {
      assignee_email: email || null,
      assignee_name: staff?.name || '',
    });
  };

  const handleTakeIt = () => {
    const me = staffList.find((s) => s.email === currentUserEmail);
    updateField('assignee', {
      assignee_email: currentUserEmail,
      assignee_name: me?.name || currentUserEmail.split('@')[0],
    });
  };

  const handleStatusChange = (status) => {
    inboundApi.put(`/tickets/${ticket.id}/status`, { status }).then((res) => onUpdate(res.data)).catch(console.error);
  };

  const handlePriorityChange = (priority) => {
    updateField('priority', { priority });
  };

  const handleTagAdd = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      const current = ticket.tags || [];
      if (!current.includes(tag)) {
        updateField('tags', { tags: [...current, tag] });
      }
      setTagInput('');
    }
  };

  const handleTagRemove = (tag) => {
    const current = ticket.tags || [];
    updateField('tags', { tags: current.filter((t) => t !== tag) });
  };

  const handleFollowerAdd = (email) => {
    const staff = staffList.find((s) => s.email === email);
    if (!staff) return;
    const current = ticket.followers || [];
    if (current.some((f) => f.email === email)) return;
    updateField('followers', {
      followers: [...current, { email: staff.email, name: staff.name }],
    });
  };

  const handleFollowerRemove = (email) => {
    const current = ticket.followers || [];
    updateField('followers', {
      followers: current.filter((f) => f.email !== email),
    });
  };

  return (
    <div className="ticket-sidebar">
      {/* Requester */}
      <div className="sidebar-field">
        <span className="sidebar-field-label">Requester</span>
        <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
          {ticket.requester?.name || ticket.requester?.email}
        </div>
        {ticket.requester?.name && (
          <div style={{ fontSize: '0.75rem', color: 'var(--topcx-text-muted)' }}>
            {ticket.requester.email}
          </div>
        )}
      </div>

      {/* Assignee */}
      <div className="sidebar-field">
        <span className="sidebar-field-label">Assignee</span>
        <Form.Select
          size="sm"
          value={ticket.assignee_email || ''}
          onChange={(e) => handleAssigneeChange(e.target.value)}
          style={{ fontSize: '0.8rem' }}
        >
          <option value="">Unassigned</option>
          {staffList.map((s) => (
            <option key={s.email} value={s.email}>{s.name} ({s.email})</option>
          ))}
        </Form.Select>
        {ticket.assignee_email !== currentUserEmail && (
          <button
            onClick={handleTakeIt}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--topcx-primary)',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '2px 0',
              textAlign: 'left',
            }}
          >
            Take it
          </button>
        )}
      </div>

      {/* Status */}
      <div className="sidebar-field">
        <span className="sidebar-field-label">Status</span>
        <Form.Select
          size="sm"
          value={ticket.status}
          onChange={(e) => handleStatusChange(e.target.value)}
          style={{ fontSize: '0.8rem' }}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </Form.Select>
      </div>

      {/* Priority */}
      <div className="sidebar-field">
        <span className="sidebar-field-label">Priority</span>
        <Form.Select
          size="sm"
          value={ticket.priority}
          onChange={(e) => handlePriorityChange(e.target.value)}
          style={{ fontSize: '0.8rem' }}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </Form.Select>
      </div>

      {/* Followers */}
      <div className="sidebar-field">
        <span className="sidebar-field-label">Followers</span>
        <div className="chip-list" style={{ marginBottom: 4 }}>
          {(ticket.followers || []).map((f) => (
            <span key={f.email} className="chip">
              {f.name || f.email}
              <span className="chip-remove" onClick={() => handleFollowerRemove(f.email)}>&times;</span>
            </span>
          ))}
        </div>
        <Form.Select
          size="sm"
          value=""
          onChange={(e) => { handleFollowerAdd(e.target.value); e.target.value = ''; }}
          style={{ fontSize: '0.75rem' }}
        >
          <option value="">Add follower...</option>
          {staffList
            .filter((s) => !(ticket.followers || []).some((f) => f.email === s.email))
            .map((s) => (
              <option key={s.email} value={s.email}>{s.name}</option>
            ))}
        </Form.Select>
      </div>

      {/* Tags */}
      <div className="sidebar-field">
        <span className="sidebar-field-label">Tags</span>
        <div className="chip-list" style={{ marginBottom: 4 }}>
          {(ticket.tags || []).map((tag) => (
            <span key={tag} className="chip">
              {tag}
              <span className="chip-remove" onClick={() => handleTagRemove(tag)}>&times;</span>
            </span>
          ))}
        </div>
        <input
          type="text"
          className="tag-input"
          placeholder="Add tag + Enter"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagAdd}
        />
      </div>

      {/* Meta info */}
      <div className="sidebar-field" style={{ marginTop: 'auto' }}>
        <span className="sidebar-field-label">Channel</span>
        <div style={{ fontSize: '0.8rem' }}>{ticket.channel}</div>
      </div>
      <div className="sidebar-field">
        <span className="sidebar-field-label">Created</span>
        <div style={{ fontSize: '0.75rem', color: 'var(--topcx-text-muted)' }}>
          {new Date(ticket.created_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}
